import React, { FC, useEffect, useState } from 'react';

import useLocales from 'hooks/useLocales';
import { JobService } from 'services/jobs/job';
import { RequestsService } from 'services/requests/request';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import RecruiterIncomingRequests from 'components/shared/RecruiterIncomingRequests';
import Popup from 'components/shared/Popups/Popup/Index';
import ProfilePopUp from 'components/shared/ProfilePopUp/Index';
import {
  CircularProgress,
  MenuItem,
  Select,
  TextField,
  Pagination
} from '@material-ui/core';
import OutgoingRequests from 'components/shared/OutgoingRequests/Index';
import NoData from 'components/shared/NoData/Index';
import Image from 'components/image/Image';
import { STAGE_TYPES, STATUS_TYPES } from 'core/constants/jobCardConstant';
import { getDateFormatter } from 'helpers/dateConverter';

import './Index.scss';
import { useDebounce } from 'helpers/useDebounce';
import OrderBySelect from 'components/shared/OrderBySelect/Index';
import {
  DEFAULT_ORDERING,
  ScoreOrdering
} from 'core/constants/ordering';

const CandidateOverview: FC = (): JSX.Element => {
  const { t } = useLocales();
  const { applications } = useSelector(
    (state: RootState) => state.applications
  );
  const [openModal, setOpenModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);

  const { request }: any = useSelector((state: RootState) => state.requests);
  const { jobs } = useSelector((state: RootState) => state.jobs);
  const [filteredApplications, setFilteredApplications] = useState<any>([]);
  const [lastFilterType, setLastFilterType] = useState<number | null>();
  const [filteredRequest, setFilteredReqFilteredRequest] = useState<any>([]);
  const [lastFilterStatus, setLastFilterStatus] = useState<number | null>();

  const [applicationCounts, setApplicationCounts] = useState<{
    incomingCount: number;
    hiredCount: number;
  }>({
    incomingCount: 0,
    hiredCount: 0
  });

  const [statusCounts, setStatusCounts] = useState<any>({
    0: 0,
    1: 0,
    2: 0,
    3: 0,
    4: 0
  });

  const [stageCounts, setStageCounts] = useState<any>({
    0: 0,
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0
  });

  const [jobPosts, setJobPosts] = useState([]);
  const [selectedJob, setSelectedJob] = useState<any>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [outgoingSearchQuery, setOutgoingSearchQuery] = useState<string>('');
  const [ordering, setOrdering] = useState<ScoreOrdering>(DEFAULT_ORDERING);
  const [outgoingOrdering, setOutgoingOrdering] =
    useState<ScoreOrdering>(DEFAULT_ORDERING);

  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const debouncedOutgoingSearchQuery = useDebounce(outgoingSearchQuery, 500);

  // Pagination state for incoming
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(6);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  // Pagination state for outgoing
  const [outgoingPage, setOutgoingPage] = useState(1);
  const [outgoingLimit, setOutgoingLimit] = useState(6);
  const [outgoingOffset, setOutgoingOffset] = useState(0);
  const [outgoingTotalCount, setOutgoingTotalCount] = useState(0);

  const handleClosePopup = () => {
    setOpenModal(false);
    setSelectedRequest(null);
  };

  useEffect(() => {
    const newStatusCounts: any = { ...statusCounts };
    Object.keys(newStatusCounts).forEach((key) => {
      newStatusCounts[key] = 0;
    });
    request?.results?.forEach((result: { status: number }) => {
      const status = result?.status;
      if (status in newStatusCounts) {
        newStatusCounts[status]++;
      }
    });
    setStatusCounts(newStatusCounts);
    setFilteredReqFilteredRequest(request?.results);
  }, [request]);

  useEffect(() => {
    const newStageCounts: any = { ...stageCounts };
    Object.keys(newStageCounts).forEach((key) => {
      newStageCounts[key] = 0;
    });

    applications?.results?.forEach((app: { stage: number }) => {
      const stage = app?.stage;
      if (stage in newStageCounts) {
        newStageCounts[stage]++;
      }
    });

    setStageCounts(newStageCounts);
  }, [applications]);

  useEffect(() => {
    if (selectedJob && jobs.results) {
      const filteredApps = applications?.results.filter(
        (app: any) => app.job.title === selectedJob
      );
      setFilteredApplications(filteredApps);
    } else {
      setFilteredApplications(applications?.results || []);
    }
  }, [selectedJob, applications]);

  useEffect(() => {
    JobService.getJobs();
  }, []);

  useEffect(() => {
    const newApplications = applications?.results?.reduce(
      (count: any, el: { stage: number }) => {
        if (el.stage === 0) {
          count++;
        }
        return count;
      },
      0
    );

    const hired = applications?.results?.reduce(
      (count: any, el: { stage: number }) => {
        if (el.stage === 5) {
          count++;
        }
        return count;
      },
      0
    );

    setApplicationCounts({
      hiredCount: hired,
      incomingCount: newApplications
    });
  }, [filteredApplications]);

  useEffect(() => {
    const jobNames = jobs?.results?.map((el: any) => el.title);
    setJobPosts(jobNames);
  }, [jobs]);

  const changeStage = async (applicationId: number, data: any) => {
    await JobService.manageJob(applicationId, data);
    getApplications();
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
  };

  const handleOutgoingSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setOutgoingSearchQuery(query);
  };

  // Update offset when page or limit changes (incoming)
  useEffect(() => {
    setOffset((page - 1) * limit);
  }, [page, limit]);
  // Update offset when page or limit changes (outgoing)
  useEffect(() => {
    setOutgoingOffset((outgoingPage - 1) * outgoingLimit);
  }, [outgoingPage, outgoingLimit]);

  // Fetch applications with backend pagination
  const getApplications = (query?: string): void => {
    const jobId = selectedJob
      ? jobs?.results?.find((job: any) => job.title === selectedJob)?.id
      : undefined;
    JobService.getApplications(jobId, query, limit, offset, ordering);
  };
  // Fetch outgoing requests with backend pagination
  const getCandidateRequests = (query?: string): void => {
    RequestsService.getRequest(
      undefined,
      query,
      outgoingLimit,
      outgoingOffset,
      outgoingOrdering
    );
  };

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchQuery, selectedJob]);

  useEffect(() => {
    setOutgoingPage(1);
  }, [debouncedOutgoingSearchQuery]);

  const handleOrderingChange = (value: ScoreOrdering): void => {
    setOrdering(value);
    setPage(1);
    setOffset(0);
  };

  const handleOutgoingOrderingChange = (value: ScoreOrdering): void => {
    setOutgoingOrdering(value);
    setOutgoingPage(1);
    setOutgoingOffset(0);
  };

  // Refetch data when pagination or filters change
  useEffect(() => {
    getApplications(debouncedSearchQuery);
  }, [debouncedSearchQuery, limit, offset, selectedJob, jobs, ordering]);

  useEffect(() => {
    getCandidateRequests(debouncedOutgoingSearchQuery);
  }, [
    debouncedOutgoingSearchQuery,
    outgoingLimit,
    outgoingOffset,
    outgoingOrdering
  ]);
  // Set total counts from API response
  useEffect(() => {
    if (applications && applications.count !== undefined) {
      setTotalCount(applications.count);
    }
  }, [applications]);
  useEffect(() => {
    if (request && request.count !== undefined) {
      setOutgoingTotalCount(request.count);
    }
  }, [request]);

  const handleStagefilter = (type: number) => {
    let filteredApps = null;
    if (lastFilterType === type) {
      filteredApps = applications?.results;
      setFilteredApplications(filteredApps);
      setLastFilterType(null);
    } else {
      filteredApps = applications?.results.filter((app) => app.stage === type);
      setFilteredApplications(filteredApps);
      setLastFilterType(type);
    }
  };

  const handleStatusFilter = (type: number) => {
    let filteredRequest = null;
    if (lastFilterStatus === type) {
      filteredRequest = request?.results;
      setFilteredReqFilteredRequest(filteredRequest);
      setLastFilterStatus(null);
    } else {
      filteredRequest = request?.results?.filter(
        (app: { status: number }) => app.status === type
      );
      setFilteredReqFilteredRequest(filteredRequest);
      setLastFilterStatus(type);
    }
  };

  const handleModal = (id: number, type?: string) => {
    let element;

    if (!type || type === 'incoming-requests' || type === 'teaser-view') {
      element = applications?.results?.find((el: any) => el.id === id);
    }

    if (!element && (!type || type === 'outgoing-requests')) {
      element = request?.results?.find((el: any) => el.id === id);
    }

    if (!element) {
      return;
    }

    setOpenModal(true);
    setSelectedRequest(element);
  };

  const cancelHandler = async (applicationId: number) => {
    await JobService.manageJob(applicationId, { status: 5 });
    getApplications();
  };

  const handleAccept = async (id: number) => {
    await JobService.acceptApplication(id);
    getApplications();
  };

  const handleDecline = async (id: number) => {
    await JobService.denyApplication(id);
    getApplications();
  };

  const stageOutgoingHandler = (id: number, data: any) => {
    RequestsService.changeRequest(data, id);
  };

  return (
    <div
      className="candidateOverviewWrapper"
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        flex: 1
      }}
    >
      <div
        className="candidateOverview"
        style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
      >
        <div className="candidateOver__right">
          <div className="candidateOver__right__top">
            <div className="candidateOver__right__top-header">
              <span className="candidateOver__right__top-title">
                {t('Incoming requests')}
              </span>
              <OrderBySelect
                value={ordering}
                onChange={handleOrderingChange}
              />
            </div>
            <div className="candidateOver__right__top__counts">
              {STAGE_TYPES?.map((stageType: any) => (
                <button
                  onClick={() => handleStagefilter(stageType.value)}
                  key={stageType.value}
                  className="candidateOver__right__top__counts-pass btn"
                >
                  {lastFilterType === stageType.value && (
                    <Image name="check.svg" />
                  )}
                  {t(stageType.to_show)} ({stageCounts[stageType.value]})
                </button>
              ))}
            </div>
            <div className="search-container">
              <TextField
                value={searchQuery}
                onChange={handleSearch}
                className="dropdown-search"
                placeholder="Search"
              />

              <Select
                displayEmpty
                value={selectedJob}
                className="select_jobs"
                placeholder={t('Select your job post')}
                onChange={(e) => setSelectedJob(e?.target?.value)}
                renderValue={(selected) => {
                  if (selected?.length === 0) {
                    return <em>{t('Select your job post')}</em>;
                  }
                  return selected;
                }}
                IconComponent={() => (
                  <>
                    <Image name="right-arrow.svg" />
                  </>
                )}
                inputProps={{ 'aria-label': 'Without label' }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {jobPosts?.map((el: any) => (
                  <MenuItem value={el} key={el}>
                    {el}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
          <div className="candidateOver__right__bottom">
            {applications == null ? (
              <div className="loadingContainer">
                <CircularProgress className="loading" />
              </div>
            ) : filteredApplications.length ? (
              filteredApplications?.map((item: any) => (
                <RecruiterIncomingRequests
                  key={item.id}
                  id={item.id}
                  name={item.candidate.name}
                  email={item.candidate.email}
                  sub_title={item.candidate.pitch}
                  icon={item.candidate.avatar}
                  score={item.score}
                  culture_score={item.culture_score}
                  date={item.created_at}
                  history={item.history}
                  industry={item.candidate.industries}
                  stage={item.stage}
                  status={item.status}
                  handleOpenModal={handleModal}
                  handleCancel={cancelHandler}
                  candidateId={item.candidate.id}
                  handleAccept={handleAccept}
                  handleDecline={handleDecline}
                  onDelete={cancelHandler}
                  showDelete
                  onStageChange={changeStage}
                  jobTitle={item?.job?.title || ''}
                />
              ))
            ) : (
              <div className="no_data">
                <NoData />
              </div>
            )}
            {/* Pagination for incoming requests */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: 16
              }}
            >
              <Pagination
                className="pagination"
                count={Math.ceil((applications?.count || 0) / limit)}
                page={page}
                onChange={(_, value) => setPage(value)}
                variant="outlined"
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </div>
          </div>
        </div>

        <div className="candidateOver__right">
          <div className="candidateOver__right__top">
            <div className="candidateOver__right__top-header">
              <span className="candidateOver__right__top-title">
                {t('Outgoing requests')}
              </span>
              <OrderBySelect
                value={outgoingOrdering}
                onChange={handleOutgoingOrderingChange}
              />
            </div>
            <div className="candidateOver__right__top__counts">
              {STATUS_TYPES?.map((statusType: any) => (
                <button
                  onClick={() => handleStatusFilter(statusType.value)}
                  key={statusType.value}
                  className="candidateOver__right__top__counts-pass btn"
                >
                  {lastFilterStatus === statusType.value && (
                    <Image name="check.svg" />
                  )}
                  {t(statusType.label)} ({statusCounts[statusType.value]})
                </button>
              ))}
            </div>
            <div className="search-container">
              <TextField
                value={outgoingSearchQuery}
                onChange={handleOutgoingSearch}
                className="dropdown-search"
                placeholder="Search"
              />
            </div>
          </div>
          <div className="candidateOver__right__bottom">
            {request == null ? (
              <div className="loadingContainer">
                <CircularProgress className="loading" />
              </div>
            ) : filteredRequest?.length ? (
              filteredRequest?.map((item: any, index: any) => (
                <OutgoingRequests
                  key={item.id}
                  id={item.id}
                  name={item.candidate.name}
                  email={item.candidate.email}
                  sub_title={item.candidate.pitch}
                  icon={item.candidate.avatar}
                  score={item.score}
                  history={item.history}
                  culture_score={item.culture_score}
                  date={item.created_at}
                  industry={item.candidate.industries}
                  stage={item.stage}
                  status={item.status}
                  stageOutgoingHandler={stageOutgoingHandler}
                  setOpen={handleModal}
                  candidateId={item.candidate.id}
                  jobTitle={
                    item?.origin_job?.title || item?.origin_profile?.title || ''
                  }
                />
              ))
            ) : (
              <div className="no_data">
                <NoData />
              </div>
            )}
            {/* Pagination for outgoing requests */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: 16
              }}
            >
              <Pagination
                className="pagination"
                count={Math.ceil((request?.count || 0) / outgoingLimit)}
                page={outgoingPage}
                onChange={(_, value) => setOutgoingPage(value)}
                variant="outlined"
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </div>
          </div>
        </div>
      </div>
      {openModal && selectedRequest && (
        <Popup
          className="popUp"
          title={t('Job')}
          description={selectedRequest?.job?.title}
          onClose={handleClosePopup}
        >
          <ProfilePopUp
            selectedRequest={selectedRequest}
            getJob={
              selectedRequest?.job ||
              selectedRequest?.origin_job ||
              selectedRequest?.origin_profile
            }
            id={selectedRequest?.id}
            onClick={() =>
              changeStage(selectedRequest?.id, {
                status: 4
              })
            }
            status={selectedRequest.status}
            isCandidate={false}
          />
        </Popup>
      )}
    </div>
  );
};

export default CandidateOverview;
